import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string } from "yup";
import { MapPin, Phone, Mail, Send, MessageSquare } from "lucide-react";
import MapLocation from "../../utilities/components/MapLocation";
import FacilityConstants from "../../utilities/constants/FacilityConstants";
import { ToastContainer, toast } from "react-toastify";
import FAQs from "../../utilities/components/FAQs";
import { submitContactInquiry } from "./ContactService";

const schema = object().shape({
  name: string().required("Full Name is required"),
  email: string().email("Invalid email format").required("Email is required"),
  subject: string().required("Subject is required"),
  message: string().required("Message is required"),
});

const ContactUs = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    try {
      const response = await submitContactInquiry(data);
      toast.success(
        response?.message ||
          "Message sent successfully! We'll get back to you soon.",
      );
      reset();
    } catch (error) {
      console.error("Contact form submission error:", error);
      toast.error(
        error.message ||
          "Failed to send message. Please try again or contact us directly.",
      );
    }
  };

  return (
    <div className="bg-white dark:bg-slate-950 transition-colors duration-300 min-h-screen">
      {/* --- Hero Section --- */}
      <section className="relative h-[40vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center z-0"
          style={{ backgroundImage: "url('/assets/images/customer_care.jpg')" }}
        />
        <div className="absolute inset-0 bg-black/60 z-10" />
        <div className="relative z-20 container mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-serif text-white mb-4 animate-fade-in-up">
            Get in Touch
          </h1>
          <p className="text-xl text-slate-200 font-light max-w-xl mx-auto animate-fade-in-up delay-100">
            We'd love to hear from you. Here's how you can reach us.
          </p>
        </div>
      </section>

      {/* --- Split Content Section --- */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Left: Contact Info */}
            <div className="space-y-12">
              <div>
                <h2 className="text-3xl font-serif font-bold text-slate-900 dark:text-white mb-6">
                  Contact Information
                </h2>
                <p className="text-slate-600 dark:text-slate-400 text-lg leading-relaxed">
                  Have a question about our venue or services? Our team is ready
                  to assist you. Reach out to us via phone, email, or visit us
                  in person.
                </p>
              </div>

              <div className="space-y-8">
                {/* Address */}
                <div className="flex items-start gap-5 group">
                  <div className="p-4 rounded-2xl bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-1">
                      Our Location
                    </h4>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        `${FacilityConstants.ADDRESS.Street}, ${FacilityConstants.ADDRESS.City}, ${FacilityConstants.ADDRESS.State} ${FacilityConstants.ADDRESS.ZipCode}`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      {FacilityConstants.ADDRESS.Street}
                      <br />
                      {FacilityConstants.ADDRESS.City},{" "}
                      {FacilityConstants.ADDRESS.State}{" "}
                      {FacilityConstants.ADDRESS.ZipCode}
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-5 group">
                  <div className="p-4 rounded-2xl bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 group-hover:bg-yellow-500 group-hover:text-white transition-colors duration-300">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-1">
                      Phone Number
                    </h4>
                    <a
                      href={`tel:${FacilityConstants.PHONE.format4}`}
                      className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors block"
                    >
                      {FacilityConstants.PHONE.format2}
                    </a>
                    <span className="text-sm text-slate-400">
                      Mon-Sat 9am-7:30pm, Sun 9am-5pm
                    </span>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-5 group">
                  <div className="p-4 rounded-2xl bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 group-hover:bg-green-600 group-hover:text-white transition-colors duration-300">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-1">
                      Email Address
                    </h4>
                    <a
                      href={`mailto:${FacilityConstants.EMAILS.info}`}
                      className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      {FacilityConstants.EMAILS.info}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Contact Form */}
            <div className="bg-slate-50 dark:bg-slate-900 p-8 md:p-10 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800">
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-3">
                <MessageSquare className="text-blue-500" size={28} />
                Send us a Message
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-8">
                Fill out the form below and we'll get back to you within 24
                hours.
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label
                      htmlFor="contact-name"
                      className="text-sm font-semibold text-slate-700 dark:text-slate-300"
                    >
                      Full Name
                    </label>
                    <input
                      id="contact-name"
                      {...register("name")}
                      className={`w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-white border ${errors.name ? "border-red-500" : "border-slate-200 dark:border-slate-700"} focus:ring-2 focus:ring-blue-500 outline-none transition-all`}
                      placeholder="John Doe"
                    />
                    <p className="text-red-500 text-xs">
                      {errors.name?.message}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <label
                      htmlFor="contact-email"
                      className="text-sm font-semibold text-slate-700 dark:text-slate-300"
                    >
                      Email Address
                    </label>
                    <input
                      id="contact-email"
                      {...register("email")}
                      className={`w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-white border ${errors.email ? "border-red-500" : "border-slate-200 dark:border-slate-700"} focus:ring-2 focus:ring-blue-500 outline-none transition-all`}
                      placeholder="john@example.com"
                    />
                    <p className="text-red-500 text-xs">
                      {errors.email?.message}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="contact-subject"
                    className="text-sm font-semibold text-slate-700 dark:text-slate-300"
                  >
                    Subject
                  </label>
                  <input
                    id="contact-subject"
                    {...register("subject")}
                    className={`w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-white border ${errors.subject ? "border-red-500" : "border-slate-200 dark:border-slate-700"} focus:ring-2 focus:ring-blue-500 outline-none transition-all`}
                    placeholder="How can we help?"
                  />
                  <p className="text-red-500 text-xs">
                    {errors.subject?.message}
                  </p>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="contact-message"
                    className="text-sm font-semibold text-slate-700 dark:text-slate-300"
                  >
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    {...register("message")}
                    rows={4}
                    className={`w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-white border ${errors.message ? "border-red-500" : "border-slate-200 dark:border-slate-700"} focus:ring-2 focus:ring-blue-500 outline-none transition-all resize-none`}
                    placeholder="Tell us more about your event..."
                  />
                  <p className="text-red-500 text-xs">
                    {errors.message?.message}
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg shadow-blue-500/30 transition-all flex items-center justify-center gap-2 hover:-translate-y-1 disabled:opacity-70 disabled:hover:translate-y-0"
                >
                  {isSubmitting ? (
                    <span className="animate-pulse">Sending...</span>
                  ) : (
                    <>
                      <Send size={20} /> Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* --- FAQs Section --- */}
      <section className="py-20 bg-slate-50 dark:bg-slate-900 border-t border-b border-slate-200 dark:border-slate-800">
        <div className="container mx-auto px-6 max-w-7xl">
          <FAQs columnLayout={true} />
        </div>
      </section>

      {/* --- Map Section --- */}
      <section className="h-[400px] md:h-[500px] w-full relative">
        <MapLocation className="w-full h-full rounded-none" />
        {/* Overlay Card on Map (Optional, for style) */}
        <div className="absolute top-8 left-1/2 -translate-x-1/2 md:left-8 md:translate-x-0 bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-white/20">
          <div className="flex items-center gap-2 text-slate-900 dark:text-white font-bold">
            <MapPin className="text-red-500" fill="currentColor" size={20} />
            <span>Find us on Google Maps</span>
          </div>
        </div>
      </section>

      <ToastContainer position="bottom-right" theme="colored" />
    </div>
  );
};

export default ContactUs;
